import { Eye, Ear, Hand } from "lucide-react";
import { SectionHeading } from "@/components/SectionHeading";
import { Reveal, Stagger, StaggerItem } from "@/components/Reveal";
import { IMAGES } from "@/config/images";

const ACTIONS = [
  "Inspect your property",
  "Meet the tenant",
  "Check a leakage",
  "Coordinate an electrician",
  "Arrange cleaning",
  "Supervise painting",
  "Handle move-in and move-out",
  "Take photographs and videos",
  "Get quotations",
  "Coordinate vendors",
  "Report back to you",
  "Execute only after your approval",
];

const PILLARS = [
  { Icon: Eye, word: "Your Eyes." },
  { Icon: Ear, word: "Your Ears." },
  { Icon: Hand, word: "Your Hands." },
];

export const EyesEarsHands = () => (
  <section className="relative overflow-hidden bg-navy-900 py-24 sm:py-32" data-testid="eyes-ears-hands-section">
    <div
      className="pointer-events-none absolute inset-0"
      style={{ background: "radial-gradient(ellipse 50% 40% at 10% 0%, rgba(201,162,39,0.1), transparent 60%)" }}
      aria-hidden="true"
    />
    <div className="container-site relative">
      <SectionHeading
        chapter="02"
        eyebrow="The Owner's Eyes Concept"
        dark
        title={
          <>
            Right Here in <span className="text-gold-400">Hyderabad.</span>
          </>
        }
      />

      <Stagger className="mt-12 flex flex-wrap gap-x-10 gap-y-6" gap={0.14}>
        {PILLARS.map(({ Icon, word }) => (
          <StaggerItem key={word}>
            <div className="flex items-center gap-5">
              <span className="flex h-14 w-14 items-center justify-center rounded-2xl border border-gold-500/30 bg-gold-500/10 sm:h-16 sm:w-16">
                <Icon className="h-6 w-6 text-gold-400 sm:h-7 sm:w-7" />
              </span>
              <span className="font-display text-4xl font-extrabold tracking-tight text-white sm:text-6xl">
                {word}
              </span>
            </div>
          </StaggerItem>
        ))}
      </Stagger>

      <div className="mt-16 grid gap-14 lg:grid-cols-2 lg:gap-20">
        <div>
          <Reveal>
            <p className="text-base leading-relaxed text-white/70 sm:text-lg">
              When you live abroad, you need more than a person who collects rent. You need someone who
              can physically be present when required. Someone who can do all of this — and report back
              to you with proof:
            </p>
          </Reveal>
          <Stagger className="mt-8 grid gap-x-8 gap-y-3 sm:grid-cols-2" gap={0.04}>
            {ACTIONS.map((a) => (
              <StaggerItem key={a}>
                <div className="flex items-center gap-3 border-b border-white/10 pb-3">
                  <span className="h-1 w-4 shrink-0 bg-gold-500" aria-hidden="true" />
                  <span className="text-sm font-medium text-white/80">{a}</span>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
          <Reveal delay={0.15}>
            <p className="mt-10 font-display text-2xl font-bold leading-snug tracking-tight text-gold-400 sm:text-3xl">
              We become your local presence in Hyderabad.
            </p>
          </Reveal>
        </div>

        <Reveal delay={0.1} className="relative">
          <div className="frame-arch mx-auto h-[420px] max-w-md overflow-hidden border border-white/10 sm:h-[520px]">
            <img
              src={IMAGES.interior1}
              alt="Well-maintained premium apartment interior in Hyderabad"
              className="duotone h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-navy-900/50 to-transparent" aria-hidden="true" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);
